interface AnalyticsService {
  initialize(): Promise<unknown>;
  trackPageView(path: string): Promise<unknown>;
  trackEvent(
    eventName: string,
    eventProperties?: Record<string, string | number | Array<string | number>>,
  ): Promise<unknown>;
  identify(userId: string, traits?: Record<string, string>): Promise<unknown>;
}

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
const DISABLE_PAGE_VIEWS =
  process.env.NEXT_PUBLIC_GA_DISABLE_PAGE_VIEWS_TRACKING === 'true';
const DISABLE_LOCALHOST_TRACKING =
  process.env.NEXT_PUBLIC_GA_DISABLE_LOCALHOST_TRACKING === 'true';

let trackingDisabledForUser = false;

function isBrowser() {
  return typeof window !== 'undefined';
}

function shouldTrack() {
  if (!isBrowser()) {
    return false;
  }

  if (trackingDisabledForUser) {
    return false;
  }

  const w = window as unknown as {
    [key: string]: unknown;
  };

  if (GA_MEASUREMENT_ID && w[`ga-disable-${GA_MEASUREMENT_ID}`] === true) {
    return false;
  }

  if (!GA_MEASUREMENT_ID) {
    // No measurement ID configured – bail out but do not crash
    return false;
  }

  if (
    DISABLE_LOCALHOST_TRACKING &&
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost'
  ) {
    return false;
  }

  return true;
}

function ensureGtagLoaded() {
  if (!shouldTrack()) {
    return;
  }

  const w = window as unknown as {
    // dataLayer holds the raw gtag() call arguments, exactly like the official GA snippet.
    // We intentionally push the `arguments` object (not a plain array) so that
    // gtag.js can process it correctly.
    dataLayer?: unknown[];
    // eslint-disable-next-line @typescript-eslint/ban-types
    gtag?: Function;
    __gaInitialized__?: boolean;
  };

  if (w.__gaInitialized__) {
    return;
  }

  // Initialize dataLayer and gtag function
  w.dataLayer = w.dataLayer || [];

  if (!w.gtag) {
    // Use the official GA snippet pattern:
    //
    //   window.dataLayer = window.dataLayer || [];
    //   function gtag(){dataLayer.push(arguments);}
    //
    // Note: we must use a regular function and push the `arguments` object,
    // not a plain array, so that gtag.js can correctly interpret the queue.
    // eslint-disable-next-line func-names, @typescript-eslint/no-explicit-any
    w.gtag = function gtag(this: unknown): void {
      // eslint-disable-next-line prefer-rest-params
      (w.dataLayer as unknown[] | undefined)?.push(arguments as any);
    };
  }

  // Load the GA script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize GA with optional automatic page view tracking
  w.gtag('js', new Date());
  w.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: !DISABLE_PAGE_VIEWS,
  });

  w.__gaInitialized__ = true;
}

export function createGoogleAnalyticsService(): AnalyticsService {
  return {
    async initialize() {
      if (!isBrowser()) {
        return;
      }

      ensureGtagLoaded();
    },

    async trackPageView(path: string) {
      if (!shouldTrack() || DISABLE_PAGE_VIEWS) {
        return;
      }

      const w = window as unknown as {
        gtag?: (...args: unknown[]) => void;
      };

      if (!w.gtag) {
        ensureGtagLoaded();
      }

      w.gtag?.('event', 'page_view', {
        page_path: path,
      });
    },

    async trackEvent(
      eventName: string,
      eventProperties?: Record<string, string | string[]>,
    ) {
      if (!shouldTrack()) {
        return;
      }

      const w = window as unknown as {
        gtag?: (...args: unknown[]) => void;
      };

      if (!w.gtag) {
        ensureGtagLoaded();
      }

      w.gtag?.('event', eventName, eventProperties ?? {});
    },

    async identify(userId: string, traits?: Record<string, string>) {
      if (!shouldTrack()) {
        return;
      }

      const w = window as unknown as {
        gtag?: (...args: unknown[]) => void;
      };

      if (!w.gtag) {
        ensureGtagLoaded();
      }

      // GA4: set user_id and user properties explicitly.
      w.gtag?.('set', 'user_id', userId);

      if (traits && Object.keys(traits).length > 0) {
        const ALLOWED_USER_PROPERTY_KEYS = ['nfx_CompanyRole'] as const;

        const userProperties = Object.fromEntries(
          Object.entries(traits).filter(([key]) =>
            ALLOWED_USER_PROPERTY_KEYS.includes(
              key as (typeof ALLOWED_USER_PROPERTY_KEYS)[number],
            ),
          ),
        );

        if (Object.keys(userProperties).length > 0) {
          w.gtag?.('set', 'user_properties', userProperties);
        }
      }
    },
  };
}
